/**
 * ONE sticky layout vocabulary for every Group surface (Batch C correction §§1–4).
 *
 * Every offset derives from the shared `--so-nav-offset` CSS variable (the app topbar
 * plus, below `lg`, the MobileNavTabs strip), so no Group screen hardcodes a pixel
 * offset and nothing has to be re-measured when the chrome changes. These are plain
 * class strings rather than a wrapper component because the surfaces they apply to are
 * structurally different (a tab row, a vertical rail, a filter aside) — sharing the
 * OFFSETS is what keeps them consistent.
 */
import React from "react";

/**
 * Measures the app chrome that is ALREADY sticky at the top of the viewport (the public
 * nav or the app topbar, plus the MobileNavTabs strip below `lg`) and publishes the total
 * as `--so-nav-offset`. Measuring beats hardcoding: the two shells have different heights,
 * the mobile strip disappears at `lg`, and nothing here has to be re-measured by hand when
 * the chrome changes. One listener, no scroll handler (correction §21).
 */
export function useStickyNavOffset() {
  React.useEffect(() => {
    const apply = () => {
      let h = 0;
      document.querySelectorAll("[data-so-chrome]").forEach((el) => {
        // `offsetParent === null` ⇒ hidden (the mobile strip above `lg`).
        if (el.offsetParent !== null) h += el.offsetHeight;
      });
      if (h > 0) document.documentElement.style.setProperty("--so-nav-offset", `${h}px`);
    };
    apply();
    window.addEventListener("resize", apply);
    return () => {
      window.removeEventListener("resize", apply);
      document.documentElement.style.removeProperty("--so-nav-offset");
    };
  }, []);
}

/** The current `--so-nav-offset` in px — what a sticky bar sits below. */
export function navOffsetPx() {
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue("--so-nav-offset");
  return parseFloat(raw) || 0;
}

/**
 * The app chrome a Group's CONTENT sits below: the sticky nav plus the Group tab bar.
 * Both come from the shared variables, so nothing here hardcodes a pixel height. Used to
 * bring a newly selected section to the top of the reading area — selecting something in
 * the rail while scrolled deep into a feed must not leave the new content off-screen.
 */
export function contentOffsetPx() {
  const styles = getComputedStyle(document.documentElement);
  const tabbar = parseFloat(styles.getPropertyValue("--so-tabbar-h")) || 0;
  // `--so-tabbar-h` is authored in rem, so convert when the computed value is not px.
  const px = styles.getPropertyValue("--so-tabbar-h").includes("rem")
    ? tabbar * (parseFloat(getComputedStyle(document.documentElement).fontSize) || 16)
    : tabbar;
  return navOffsetPx() + px;
}

/**
 * Brings a column's top into view under the chrome, and ONLY when the reader has scrolled
 * past it. Scrolling someone DOWN to a section they can already see would be the more
 * annoying bug, so this never does that.
 */
export function scrollSectionIntoView(el) {
  if (!el) return;
  const gap = 12;
  const top = window.scrollY + el.getBoundingClientRect().top - contentOffsetPx() - gap;
  if (window.scrollY > top) {
    window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  }
}

/** A primary in-page tab bar: sticks directly under the app chrome. */
export const STICKY_TABBAR =
  "sticky top-[var(--so-nav-offset)] z-20 -mx-1 bg-background/95 px-1 py-2 backdrop-blur "
  + "supports-[backdrop-filter]:bg-background/80";

/**
 * The Group's primary tab bar reads as ONE teal bar with white labels. The active tab is
 * still unmistakable without breaking that: a lighter translucent pill plus a heavier
 * weight, so every label stays white (§1 — "active tab remains obvious").
 */
export const GROUP_TABS_LIST =
  "h-auto w-full justify-start gap-0.5 rounded-lg bg-primary p-1 text-primary-foreground";

export const GROUP_TAB_TRIGGER =
  "rounded-md px-3 py-1.5 text-primary-foreground/80 transition-colors "
  + "hover:bg-white/10 hover:text-primary-foreground "
  + "focus-visible:ring-white/70 focus-visible:ring-offset-primary "
  + "data-[state=active]:bg-white/25 data-[state=active]:font-[680] "
  + "data-[state=active]:text-primary-foreground data-[state=active]:shadow-none";

/**
 * A secondary rail / sidebar / filter aside inside a tab: sticks below the sticky tab bar
 * and takes its OWN scrollbar once it is taller than the space it has. It sticks at every
 * width — on phones these rails are horizontal strips, so staying put is exactly the
 * "compact selector one tap away" behaviour a narrow screen wants, and the internal
 * scrollbar only kicks in from `lg` where the rail is a tall column. `overflow-hidden`
 * clips the phone chip row's horizontal overflow HERE: the row scrolls internally, but
 * without a clip on this box that overflow escapes and widens the whole page.
 */
export const STICKY_RAIL =
  "sticky top-[calc(var(--so-nav-offset)+var(--so-tabbar-h))] z-10 overflow-hidden "
  + "bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 "
  + "lg:max-h-[calc(100dvh-var(--so-nav-offset)-var(--so-tabbar-h)-1.5rem)] "
  + "lg:overflow-y-auto lg:overscroll-contain";
